/**
 * SocialEmbedBlock — Web renderer for SOCIAL_EMBED content blocks.
 *
 * Embeds social media posts using platform-specific oEmbed iframes.
 * Supports Twitter/X, TikTok, YouTube, and Instagram.
 */

import { SurfaceSocialEmbedConfigResponse } from "@/hooks/hub/useGetHub";
import "./SocialEmbedBlock.scss";

interface SocialEmbedBlockProps {
    config?: SurfaceSocialEmbedConfigResponse;
    sectionTitle?: string;
}

/**
 * Convert a social URL to an embeddable iframe src.
 * Falls back to a link if the URL format isn't recognized.
 */
function getEmbedSrc(platform: string, url: string): string | null {
    if (!url) return null;

    switch (platform) {
        case "youtube": {
            // Support youtube.com/watch?v=X and youtu.be/X
            const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
            return match ? `https://www.youtube.com/embed/${match[1]}` : null;
        }
        case "twitter": {
            // Twitter embeds use the publish API
            return null; // Twitter requires script-based embed, use blockquote fallback
        }
        case "tiktok": {
            // TikTok embed format
            const match = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
            return match ? `https://www.tiktok.com/embed/v2/${match[1]}` : null;
        }
        case "instagram": {
            // Instagram embeds
            return null; // Instagram requires script-based embed, use link fallback
        }
        default:
            return null;
    }
}

export const SocialEmbedBlock = ({ config, sectionTitle }: SocialEmbedBlockProps) => {
    if (!config?.embedUrl) return null;

    const embedSrc = getEmbedSrc(config.platform, config.embedUrl);
    const isSafeUrl = (/^https?:\/\//).test(config.embedUrl);

    return (
        <div className="social-embed-block">
            {sectionTitle && <h2 className="section-title">{sectionTitle}</h2>}
            <div className="social-embed-block__container">
                {embedSrc
                    ? <iframe
                        src={embedSrc}
                        className="social-embed-block__iframe"
                        allowFullScreen
                        loading="lazy"
                        title={`${config.platform} embed`}
                    />
                    : isSafeUrl
                        ? <a
                            href={config.embedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-embed-block__link"
                        >
                            View on {config.platform.charAt(0).toUpperCase() + config.platform.slice(1)}
                        </a>
                        : null
                }
            </div>
        </div>
    );
};
